import { observable, makeObservable } from 'mobx'
import * as R from 'ramda'
import React from 'react'
import i18n from 'i18next'
import Anchor from '../../UniformComponents/Anchor'
import Badge from '../../UniformComponents/Badge'
import Tooltip from '../../UniformComponents/Tooltip'
import { getUserLink, unescapeHtmlTags } from '../../utils/helpers'
import LinkButton from '../../UniformComponents/LinkButton'

class SurveySearchStore {
    @observable filteredList = []

    @observable surveyLicenceModalVisible = false

    @observable selectedSurveyLicense = ''

    @observable showChooseIndustry = false

    @observable noLicenceModalVisible = false

    creatingRapid = false

    // Maxbi statuses
    maxbi = {
        completed: {
            id: 1, type: 'success', label: 'Completed', icon: 'fa-regular fa-check',
        },
        inprogress: {
            id: 2, type: 'warning', label: 'In progress', icon: 'fa-regular fa-arrow-down',
        },
        incomplete: {
            id: 3, type: 'error', label: 'Incomplete', icon: 'fa-regular fa-ban',
        },
        archived: {
            id: 4, type: 'archive', label: 'Archived',
        },
    }

    searchSurveyType = {
        maxbi: 'Maxbi Entries',
        survey: 'Surveys',
        report: 'Reports',
    }

    surveyStatus = new Map([
        [2, { type: 'purple', label: 'In development' }],
        [1, { type: 'blue', label: 'Open' }],
        [3, { type: 'grey', label: 'Closed' }],
        [4, { type: 'yellow', label: 'Archived' }],
    ])

    statuses = []

    status = []

    surveys = []

    surveyRespondentTypes = {}

    surveyTypes = []

    respondentTypes = []

    productTypes = []

    years = [{ label: 'View all', value: '' }]

    year = { label: 'View all', value: '' }

    products = [{ label: 'View all', value: '' }]

    product = { label: 'View all', value: '' }

    checkBoxFilters = {}

    industries = []

    columns=[]

    // for maxbi
    isMaxbi = false

    constructor(store) {
        makeObservable(this)
        this.appStore = store
    }

    getSurveysData = async (url) => {
        const surveys = await this.appStore.http.get(url)
        return surveys.map((s) => ({ ...s, productName: unescapeHtmlTags(s.productName) }))
    }

    getIndustriesData = async () => (
        this.appStore.http.get('/client/industry')
    )

    showSurveyLicenceModal = () => {
        this.surveyLicenceModalVisible = true
    }

    hideSurveyLicenceModal = () => {
        this.surveyLicenceModalVisible = false
    }

    closeSurveyLicenceModal = () => {
        this.creatingRapid = false
        this.selectedSurveyLicense = ''
        this.hideSurveyLicenceModal()
    }

    selectSurveyLicense = async (type) => {
        if (type === 'rapid') {
            this.creatingRapid = true
        }
        this.selectedSurveyLicense = type

        this.hideSurveyLicenceModal()
        this.toggleIndustryChoose()
    }

    showNoLicenceModal = () => {
        this.noLicenceModalVisible = true
    }

    hideNoLicenceModal = () => {
        this.noLicenceModalVisible = false
    }

    handleCreateSurvey = () => {
        const licenses = {
            pro: this.appStore.hasProSurveysEnabled(),
            rapid: this.appStore.hasRapidSurveysEnabled(),
        }

        if (Object.values(licenses).every((l) => !l)) {
            this.showNoLicenceModal()
            return
        }

        if (Object.values(licenses).every((l) => !!l)) {
            this.showSurveyLicenceModal()
            return
        }

        this.creatingRapid = licenses.rapid
        this.toggleIndustryChoose('survey')
    }

    toggleIndustryChoose = () => {
        const { showChooseIndustry, industries } = this
        const industryChooseOpen = !showChooseIndustry

        if (!industryChooseOpen) {
            this.creatingRapid = false
            this.selectedSurveyLicense = ''
        }

        if (industryChooseOpen) {
            if (industries.length === 1) {
                this.openNewSurvey(industries[0].code)
                return
            }
        }

        this.showChooseIndustry = industryChooseOpen
    }

    closeIndustryChoose = () => {
        this.showChooseIndustry = false
    }

    openNewSurvey = (industryCode) => {
        this.appStore.goTo('full', `/survey/0/${industryCode}${this.creatingRapid ? '?surveytype=R' : ''}`)
    }

    // format the response data to create respondentTypes & surveyTypes
    formatSurveyRespondentTypes = (industries, surveyRespondentTypes) => {
        const industryNames = {}
        industries.forEach((i) => {
            industryNames[i.code] = i.name
        })

        const respondentTypes = surveyRespondentTypes.respondentTypes.reduce((map,
            { respondentType, respondentTypeName, industryCode }) => {
            map[respondentType] = {
                selected: true,
                name: `${industryNames[industryCode]} - ${respondentTypeName}`,
            }
            return map
        }, {})

        const surveyTypes = surveyRespondentTypes.surveyTypes.reduce((map,
            { surveyType, surveyTypeName }) => {
            map[surveyType] = {
                selected: true,
                name: surveyTypeName,
            }
            return map
        }, {})

        return [respondentTypes, surveyTypes]
    }

    getSurveyRespondentTypesData = async () => {
        this.surveyRespondentTypes = await this.appStore.http.get('/surveyrespondenttypes?fromSurveys=1')
        return this.formatSurveyRespondentTypes(this.industries, this.surveyRespondentTypes)
    }

    getProductData = async () => {
        let products = await this.appStore.http.get('/product')
        products = products.filter((prod) => prod.status !== 5)
        return this.products.concat(R.sortWith([
            R.ascend(
                R.compose(
                    R.toLower,
                    R.prop('label'),
                ),
            ),
        ])(products.map(({ productId, name, edition }) => ({
            value: productId,
            label: `${name}${edition ? ` - ${edition}` : ''}`,
        }))))
    }

    getProductTypes = () => {
        const { prodductMap } = this.appStore.client
        const formatted = Object.values(prodductMap).reduce((map, industry) => {
            Object.values(industry.children || {}).forEach(({ value, label }) => {
                map[value] = { selected: true, name: label }
            })
            return map
        }, {})
        return formatted
    }

    getYears = (surveys) => {
        const surveyPossibleYears = surveys
            .reduce((possibleYears, { year }) => {
                if (!Object.hasOwnProperty.call(possibleYears, year)) {
                    possibleYears[year] = year
                }
                return possibleYears
            }, {})

        return this.years.concat(Object.keys(surveyPossibleYears).map((year) => ({
            value: year,
            label: year,
        })))
    }

    // get the product id from the ProductSearch route
    checkProductParam = (query) => {
        if (query.has('event')) {
            const productIdParam = parseInt(query.get('event'), 10)
            const foundProduct = this.products.find((product) => (product.value === productIdParam))
            if (foundProduct) this.product = foundProduct
        }
    }

    // get the status id from the Home route
    checkStatusParam = (query) => {
        if (query.has('status')) {
            const statusIdParam = parseInt(query.get('status'), 10)
            if (this.statuses.findIndex((s) => s.value === statusIdParam) > -1) {
                this.status = [statusIdParam]
            }
        } else if (this.isMaxbi) {
            this.status = ['completed', 'inprogress', 'incomplete']
        } else this.status = [1, 2, 3]
    }

    setStatuses = (surveySearchType) => {
        this.statuses = surveySearchType === 'maxbi'
            ? Object.keys(this.maxbi).map(
                (key) => ({
                    label: this.maxbi[key].label,
                    value: key,
                }),
            )
            : Array.from(this.surveyStatus, ([key, { label }]) => (
                {
                    label,
                    value: parseInt(key, 10),
                }
            ))
    }

    init = async (query, surveySearchType) => {
        if (surveySearchType === 'maxbi') {
            this.surveys = await this.getSurveysData('/maxbi')
            this.isMaxbi = true
        } else {
            [this.surveys, this.industries] = await Promise.all([
                this.getSurveysData('/survey'), this.getIndustriesData(),
            ])
        }
        this.setStatuses(surveySearchType)
        this.products = await this.getProductData()
        this.productTypes = this.getProductTypes()
        const productIds = this.products.map((prod) => prod.value)
        this.surveys = this.surveys.filter((surv) => productIds.includes(surv.productId))
        if (process.env.USE_SURVEY_BRANCHING) {
            this.surveys = this.groupBranches(this.surveys)
        }
        this.filteredList = this.surveys
        if (this.industries.length) {
            [this.respondentTypes, this.surveyTypes] = await this.getSurveyRespondentTypesData()
        }
        if (this.surveys.length) {
            this.years = this.getYears(this.surveys)
        }
        this.checkProductParam(query)
        this.checkStatusParam(query)
        this.search()
    }

    groupBranches = (surveys) => {
        const childSurveys = surveys.reduce((cs, s) => {
            if (s.parentSurveyId) {
                const parentId = s.parentSurveyId.toUpperCase()
                const newCs = { ...cs }
                newCs[parentId] = cs[parentId] || []
                newCs[parentId].push(s)
                return newCs
            }
            return cs
        }, {})

        return surveys.reduce((survs, s) => {
            if (s.parentSurveyId) return survs
            const possParentId = s.surveyId.toUpperCase()
            if (!childSurveys[possParentId]) return [...survs, s]
            return [
                ...survs,
                {
                    ...s,
                    children: childSurveys[possParentId],
                },
            ]
        }, [])
    }

    toggleStatus = ({ value, name }) => {
        if (value) this.status.push(name)
        else this.status = this.status.filter((s) => s !== name)
        this.search()
    }

    toggleSurveyType = ({ value, name }) => {
        this.surveyTypes[name].selected = value
        this.search()
    }

    toggleRespondentType = ({ value, name }) => {
        this.respondentTypes[name].selected = value
        this.search()
    }

    toggleProductType = ({ value, name }) => {
        this.productTypes[name].selected = value
        this.search()
    }

    changeProduct = ({ label, value }) => {
        this.product = { label, value }
        this.search()
    }

    changeYear = ({ label, value }) => {
        this.year = R.isEmpty(value)
            ? { label, value }
            : { label, value: parseInt(value, 10) }
        this.search()
    }

    search = () => {
        const {
            surveys, surveyTypes, respondentTypes, productTypes,
        } = this
        const searchStr = R.pathOr('', ['searchModule', 'searchStr'], this)
        const activeFiltered = surveys
            .filter(({ status }) => (
                this.status.includes(status)
            ))
            .filter(({ surveyType, respondentType }) => (
                this.isMaxbi
                || (R.pathOr(false, [surveyType, 'selected'])(surveyTypes)
                && R.pathOr(false, [respondentType, 'selected'])(respondentTypes))
            ))
            .filter(({ producttypecode }) => (
                R.pathOr(false, [producttypecode, 'selected'])(productTypes)
            ))
            .filter(({ productId }) => (
                R.isEmpty(this.product.value) || this.product.value === productId
            ))
            .filter(({ year }) => (
                R.isEmpty(this.year.value) || this.year.value === year
            ))
        if (searchStr === '') {
            this.filteredList = activeFiltered
        } else if (!Number.isNaN(parseInt(searchStr, 10))) {
            this.filteredList = activeFiltered
                .filter((d) => d.productId === parseInt(searchStr, 10))
        } else {
            const re = new RegExp(searchStr, 'i')
            this.filteredList = activeFiltered
                .filter((d) => (
                    d.surveyName.match(re)
                        || d.surveyId.match(re)
                        || d.productName.match(re)
                ))
        }
    }

    resetFilter = () => {
        if (!this.isMaxbi) {
            [this.respondentTypes, this.surveyTypes] = this.formatSurveyRespondentTypes(
                this.industries, this.surveyRespondentTypes,
            )
        }
        this.status = this.isMaxbi ? ['completed', 'inprogress', 'incomplete'] : [1, 2, 3]
        this.year = { label: 'View all', value: '' }
        this.product = { label: 'View all', value: '' }
        this.search()
    }

    getRowClickPath = (status) => {
        switch (status) {
            case 1: return 'report' // open
            case 2: return 'setup' // in development
            case 3: return 'report' // closed
            case 4: return '' // archived
            default: return null
        }
    }

    getParentStatus = (status) => {
        if (status === 3) return 'Parent - Closed'
        if (status === 4) return 'Parent - Archived'
        return 'Parent'
    }

    getParentStatusType = (status) => {
        if (status === 3) return 'grey'
        if (status === 4) return 'yellow'
        return 'blue'
    }

    // generate columns for specific type
    setColumns = (surveySearchType, store) => {
        const commonCols = {
            productName: {
                name: i18n.t('event', 'Event'),
                key: 'productName',
            },
            surveyName: {
                name: i18n.t('survey', 'Survey'),
                key: 'surveyName',
                cellClass: 'bold',
                dataTransform: (value, row) => (row.parentSurveyId
                    ? (
                        <>
                            <i style={{ marginRight: '4px' }} className="fa-regular fa-sitemap exploriBlue" />
                            {value}
                        </>
                    )
                    : value),
            },
        }

        switch (surveySearchType) {
            case 'report': {
                this.columns = [
                    {
                        name: i18n.t('status', 'Status'),
                        key: 'status',
                        cellClass: 'status',
                        dataTransform: (status) => (
                            <Badge
                                cssClass="status-badge"
                                type={this.surveyStatus.get(status).type}
                                label={i18n.t(`status_${status}`, this.surveyStatus.get(status).label)}
                            />
                        ),
                    },
                    commonCols.productName,
                    commonCols.surveyName,
                    {
                        name: 'End',
                        key: 'closeDate',
                        dataTransform: (date) => (
                            new Date(date).getFullYear()
                        ),
                    },
                    {
                        name: 'Completed',
                        key: 'completed',
                        dataTransform: (completed) => (
                            <Badge
                                type="success"
                                cssClass="rounded"
                                label={completed}
                                icon="fa-regular fa-check"
                            />
                        ),
                    },
                    {
                        name: 'Dropped',
                        key: 'dropped',
                        dataTransform: (dropped) => (
                            <Badge
                                type="warning"
                                cssClass="rounded"
                                label={dropped}
                                icon="fa-regular fa-arrow-down"
                            />
                        ),
                    },
                    {
                        name: 'Screened',
                        key: 'screened',
                        dataTransform: (screened) => (
                            <Badge
                                type="error"
                                cssClass="rounded"
                                label={screened}
                                icon="fa-regular fa-ban"
                            />
                        ),
                    },
                    {
                        name: '',
                        key: 'surveyId',
                        cellClass: 'center overflow',
                        dataTransform: (surveyId, row) => (
                            <div className="survey-btn-wrapper">
                                <Anchor
                                    cssClass="icon-link-btn"
                                    onClick={(e) => e.stopPropagation()}
                                    href={`/survey/${surveyId}/report`}
                                >
                                    <Tooltip
                                        text={i18n.t('reports_view', 'View report')}
                                        direction="left"
                                    />
                                    <i className="fa-light fa-chart-column" />
                                </Anchor>
                                {(R.path(['userData', 'role'], store) === 150 && row.surveyType === 'PE')
                                    && (
                                        <Anchor
                                            cssClass="icon-link-btn"
                                            onClick={(e) => e.stopPropagation()}
                                            href={`/survey/${surveyId}/company/dashboard/?productId=${row.productId}`}
                                        >
                                            <Tooltip
                                                text={i18n.t('s_tab_productDash')}
                                                direction="left"
                                            />
                                            <i className="fa-light fa-gauge-simple-max" />
                                        </Anchor>
                                    )}
                            </div>
                        ),
                    },
                ]
                break
            }
            case 'maxbi': {
                this.columns = [
                    commonCols.productName,
                    {
                        name: 'Year',
                        key: 'year',
                    },
                    {
                        name: 'Status',
                        key: 'status',
                        cellClass: 'status',
                        dataTransform: (status) => (
                            <Badge
                                type={this.maxbi[status].type}
                                icon={this.maxbi[status].icon}
                                label={i18n.t(`maxbiSearch.status_${this.maxbi[status].id}`, this.maxbi[status].label)}
                            />
                        ),
                    },
                    {
                        name: 'Required action',
                        key: 'surveyUserId',
                        dataTransform: (surveyUserId, { status, publicId }) => (
                            (status === 'inprogress' || status === 'incomplete')
                                ? (
                                    <LinkButton
                                        cssClass={`action-btn ${status}`}
                                        target="_blank"
                                        href={
                                            getUserLink(
                                                publicId, surveyUserId, true,
                                            )
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation()
                                        }}
                                        label={i18n.t('maxbiSearch.complete', 'Complete')}
                                        isExternal
                                    >
                                        <i className="far fa-arrow-right" />
                                    </LinkButton>
                                ) : null
                        ),
                    },
                    {
                        name: '',
                        key: 'surveyId',
                        cellClass: 'overflow',
                        dataTransform: (surveyId, row) => (
                            <div className="survey-btn-wrapper">
                                {row.status === 'completed' ? (
                                    <>
                                        <Anchor
                                            cssClass="icon-link-btn"
                                            onClick={(e) => e.stopPropagation()}
                                            href={`/survey/${surveyId}/company/dashboard/?productId=${row.productId}`}
                                            isExternal
                                        >
                                            <Tooltip
                                                text={i18n.t('product_dash', 'View dashboard')}
                                                direction="left"
                                            />
                                            <i className="fa-light fa-gauge-simple-max" />
                                        </Anchor>
                                        <Anchor
                                            cssClass="icon-link-btn"
                                            onClick={(e) => e.stopPropagation()}
                                            href={`/survey/${surveyId}/report`}
                                            isExternal
                                        >
                                            <Tooltip
                                                text={i18n.t('home.table.surveyBtn.viewReport')}
                                                direction="left"
                                            />
                                            <i className="fa-light fa-chart-column" />
                                        </Anchor>
                                    </>
                                ) : null}
                                <Anchor
                                    cssClass="icon-link-btn"
                                    onClick={(e) => e.stopPropagation()}
                                    href={`/survey/${surveyId}`}
                                    isExternal
                                >
                                    <Tooltip
                                        text="Edit Entry"
                                        direction="left"
                                    />
                                    <i className="fa-light fa-edit" />
                                </Anchor>
                            </div>

                        ),
                    },
                ]
                break
            }
            case 'survey': {
                this.columns = [
                    commonCols.productName,
                    commonCols.surveyName,
                    {
                        name: i18n.t('status', 'Status'),
                        key: 'status',
                        cellClass: 'status',
                        dataTransform: (status, { isParent }) => (
                            <Badge
                                cssClass="rounded status-badge"
                                type={isParent ? this.getParentStatusType(status) : this.surveyStatus.get(status).type}
                                label={isParent ? this.getParentStatus(status) : i18n.t(`status_${status}`, this.surveyStatus.get(status).label)}
                            />
                        ),
                    },
                    {
                        name: i18n.t('survey_closing_date', 'Survey closing date'),
                        key: 'closeDate',
                        dataTransform: (closeDate) => {
                            const date = new Date(closeDate)
                            return date.toLocaleDateString('en-GB', {
                                day: '2-digit', month: 'short', year: 'numeric',
                            })
                        },
                    },
                    {
                        name: i18n.t('completed', 'Completed'),
                        key: 'completed',
                        headerClass: 'center',
                        dataTransform: (completed) => (
                            <Badge
                                type="grey"
                                cssClass="rounded center"
                                label={completed}
                            />
                        ),
                    },
                    {
                        name: '',
                        key: 'surveyId',
                        cellClass: 'overflow',
                        dataTransform: (surveyId, row) => (
                            <div className="survey-btn-wrapper">
                                {row.status !== 2 && ( // 2 = in development
                                    <>
                                        { row.surveyType !== 'GE' && (
                                            <Anchor
                                                cssClass="icon-link-btn"
                                                onClick={(e) => e.stopPropagation()}
                                                href={`/survey/${surveyId}/company/dashboard/?productId=${row.productId}`}
                                            >
                                                <Tooltip
                                                    text={i18n.t('reports_dash', 'Event dashboard')}
                                                    direction="left"
                                                />
                                                <i className="fa-light fa-gauge-simple-max" />
                                            </Anchor>
                                        )}
                                        <Anchor
                                            cssClass="icon-link-btn"
                                            onClick={(e) => e.stopPropagation()}
                                            href={`/survey/${surveyId}/report`}
                                        >
                                            <Tooltip
                                                text={i18n.t('reports_view', 'View report')}
                                                direction="left"
                                            />
                                            <i className="fa-light fa-chart-column" />
                                        </Anchor>
                                    </>
                                )}
                                <Anchor
                                    cssClass="icon-link-btn"
                                    onClick={(e) => e.stopPropagation()}
                                    href={`/survey/${surveyId}/setup`}
                                >
                                    <Tooltip
                                        text={i18n.t('reports_edit', 'Edit report')}
                                        direction="left"
                                    />
                                    <i className="fa-light fa-edit" />
                                </Anchor>
                            </div>
                        ),
                    },
                ]
                break
            }
            default: break
        }
    }
}

export default SurveySearchStore
